<template>
    <div class="tw-w-full">
        <div class="tw-w-full tw-box-border tw-my-4">
            <h3 class="tw-mb-4 tw-text-lg tw-font-semibold tw-leading-6 tw-text-center">
                {{ translate('offers.section_title') }}
            </h3>
        </div>
        <div class="tw-w-full tw-box-border tw-bg-light-grey">
            <h4 class="tw-text-grey tw-uppercase tw-font-semibold">{{ translate('offers.offer') }}</h4>
            <OfferItem v-for="offer in offers" :offer="offer" :key="offer.id" />
            <table class="summary-table">
                <tbody>
                    <tr>
                        <td align="left" class="tw-text-sm tw-py-1">
                            {{ translate('offers.amount') }}
                        </td>
                        <td align="right" class="tw-text-sm tw-py-1">{{ totalPeople }}</td>
                    </tr>
                    <tr>
                        <td align="left" class="tw-text-sm tw-py-1">
                            {{ translate('offers.min_people') }}
                        </td>
                        <td align="right" class="tw-text-sm tw-py-1">{{ minPeople }}</td>
                    </tr>
                    <tr>
                        <td align="left" class="tw-text-sm tw-py-1">
                            {{ translate('offers.max_people') }}
                        </td>
                        <td align="right" class="tw-text-sm tw-py-1">{{ maxPeople }}</td>
                    </tr>
                    <tr>
                        <td align="left" class="tw-text-sm tw-py-1">
                            {{ translate('offers.price') }}
                        </td>
                        <td align="right" class="tw-text-sm tw-py-1 tw-font-bold total">
                            {{ priceSum }}&nbsp;{{ currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="appliedCoupon">
                        <td align="left" class="tw-text-sm tw-py-1">
                            {{ translate('offers.coupon_discount') }}
                        </td>
                        <td align="right" class="tw-text-sm tw-py-1">-{{ couponDiscount }}&nbsp;{{ currencyCode }}</td>
                    </tr>
                </tbody>
            </table>
            <transition name="fade">
                <div
                    class="center tw-mt-4 tw-mb-6 tw-text-dark-grey tw-text-base tw-font-normal"
                    v-show="explanationVisible"
                >
                    {{ translate('offers.fee_description') }}
                </div>
            </transition>
        </div>
        <div class="tw-w-full">
            <!-- coupon -->
            <fieldset class="input-wrapper tw-my-4 tw-mx-1">
                <a
                    class="tw-text-base tw-underline tw-font-semibold coupon-code"
                    href="#"
                    @click.prevent="couponInput"
                    >{{ translate('offers.voucher') }}</a
                >
                <input
                    type="text"
                    class="input-field-app"
                    name="coupon"
                    id="coupon"
                    v-model="couponName"
                    v-show="displayCouponInput"
                />
            </fieldset>
            <!-- end coupon -->
            <div class="tw-my-8 tw-flex tw-flex-col md:tw-flex-row tw-items-center tw-justify-center">
                <span class="tw-text-xl tw-font-semibold tw-uppercase total-price"
                    >{{ translate('offers.total') }}:</span
                >
                <span class="tw-text-xl tw-font-semibold tw-uppercase total-price">
                    {{ totalCost }}&nbsp;{{ currencyCode }}
                </span>
            </div>
            <div class="errors" v-if="backendErrorMessages.length">
                <p class="error-message" v-for="message in backendErrorMessages" :key="message">{{ message }}</p>
            </div>
        </div>
        <div class="btn-container tw-mb-4">
            <button @click.prevent="createRequest" class="btn btn-tertiary btn-md btn-fw" :disabled="!validated">
                {{ translate('offers.next') }}
            </button>
        </div>
        <div
            class="tw-text-xs tw-leading-tight tw-text-center tw-border tw-border-solid tw-border-blue tw-rounded-4 tw-p-2 tw-text-dark-grey support-message"
            v-html="translate('booking.info', { phone: phoneLink, email: emailLink })"
        ></div>
    </div>
</template>

<script>
import minusIcon from '@images/minus.svg';
import plusIcon from '@images/plus.svg';
import groupPeopleIcon from '@images/group-people.svg';
import questionMarkIcon from '@images/question.svg';
import i18n from '@js/vue/mixins/i18n';
import OfferItem from './shared/OfferItem.vue';
import { BOOKING_VIEWS } from '@js/vue/booking-views.js';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { trackCouponCodeClick, trackCouponCodeApply, trackBookingDetailsSubmit } from '@js/vue/dataLayerUtils.js';
import { contactInfo } from '@js/vue/contact-info.js';

export default {
    name: 'Offers',
    mixins: [i18n],
    inject: ['currencyCode', 'translations'],
    emits: ['changeView', 'error'],
    components: { OfferItem },
    created() {
        if (this.items) this.populateCart();
        if (this.appliedCoupon) {
            this.couponName = this.appliedCoupon.name;
            this.displayCouponInput = true;
        }
    },
    data() {
        return {
            backendErrorMessages: [],
            displayCouponInput: false,
            couponName: '',
            minusIcon,
            plusIcon,
            questionMarkIcon,
            groupPeopleIcon,
            explanationVisible: false,
            BOOKING_VIEWS,
            phoneLink: `<a class="tw-whitespace-nowrap tw-underline" href="tel:${contactInfo.phoneNumber}">${contactInfo.phoneNumber}</a>`,
            emailLink: `<a class="tw-whitespace-nowrap tw-underline" href="mailto:${contactInfo.emailAddress}">${contactInfo.emailAddress}</a>`,
        };
    },
    methods: {
        ...mapMutations('booking', ['SET_REGION_CATEGORY']),
        ...mapActions('booking', ['populateCart', 'postBookingData']),
        //TODO Break this method down into multiple smaller methods
        createRequest() {
            const data = {
                items: Array.from(this.cart, ([id, quantity]) => ({ id, quantity })),
                date: this.date,
                time: this.time,
            };
            if (this.couponName) {
                data.coupon = this.couponName;
            }
            // clear backend errors before sending
            this.backendErrorMessages = [];
            this.postBookingData({ id: this.id, data })
                .then(() => {
                    if (this.getAppliedCoupon) {
                        trackCouponCodeApply(this.title);
                    }
                    trackBookingDetailsSubmit(
                        this.title,
                        this.regionCategorySlugs['region-slug'],
                        this.regionCategorySlugs['category-slug'],
                        this.totalPrice
                    );
                    this.$emit('changeView', BOOKING_VIEWS.CUSTOMER_INFO_AND_PAYMENT);
                })
                .catch((error) => {
                    if (error.response) {
                        const { status } = error.response;
                        if (status === 422) {
                            // unprocessable data, display backend errors
                            this.backendErrorMessages = error.response.data.errors;
                        } else if (status === 419 || status === 404) {
                            // 419 csrf token mismatch (session expired)
                            // 404 cart not in session (probably expired session)
                            console.dir(error);
                            this.$emit('error', 'session_expired');
                        } else {
                            // something else went wrong
                            console.dir(error);
                            this.$emit('error', 'something_went_wrong');
                        }
                    } else {
                        // Handle other types of errors (e.g., network issues)
                        console.error('An unexpected error occurred:', error);
                        this.$emit('error', 'unexpected_error');
                    }
                });
        },
        couponInput() {
            trackCouponCodeClick(this.title);
            this.displayCouponInput = true;
        },
    },
    computed: {
        ...mapGetters('booking', [
            'getDate',
            'getTime',
            'getCart',
            'getPriceSum',
            'getOffers',
            'getMinPeople',
            'getMaxPeople',
            'getServiceId',
            'getServiceTitle',
            'getTotalPeople',
            'getRegionCategorySlugs',
            'getCouponDiscount',
            'getTotalCost',
            'getTotalPrice',
            'getAppliedCoupon',
            'getItems',
        ]),
        id() {
            return this.getServiceId;
        },
        date() {
            return this.getDate;
        },
        time() {
            return this.getTime;
        },
        title() {
            return this.getServiceTitle;
        },
        cart() {
            return this.getCart;
        },
        offers() {
            return this.getOffers;
        },
        minPeople() {
            return this.getMinPeople;
        },
        maxPeople() {
            return this.getMaxPeople;
        },
        totalPeople() {
            return this.getTotalPeople;
        },
        totalPrice() {
            return this.getTotalPrice;
        },
        appliedCoupon() {
            return this.getAppliedCoupon;
        },
        regionCategorySlugs() {
            return this.getRegionCategorySlugs;
        },
        hasEnoughPeople() {
            return this.totalPeople >= this.minPeople && this.totalPeople <= this.maxPeople;
        },
        isValidDateTime() {
            return this.date && this.time;
        },
        cartNotEmpty() {
            return this.cart.size > 0;
        },
        validated() {
            return this.hasEnoughPeople && this.isValidDateTime && this.cartNotEmpty;
        },
        priceSum() {
            return this.getPriceSum;
        },
        couponDiscount() {
            return this.getCouponDiscount;
        },
        totalCost() {
            return this.getTotalCost;
        },
        items() {
            return this.getItems;
        },
    },
};
</script>
